
import { useState } from 'react';
import { supabase } from '../lib/supabase';

interface UploadResult {
  url: string;
  path: string;
  fileName: string;
}

export const useSupabaseStorage = () => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const uploadFile = async (file: File, bucketName: string = 'uploads'): Promise<UploadResult> => {
    try {
      setUploading(true);
      setUploadProgress(0);

      // SAFEGUARD: Log upload attempt for debugging
      console.log(`[UPLOAD SAFEGUARD] Uploading ${file.type} file: ${file.name} to bucket: ${bucketName}`);

      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `uploads/${fileName}`;

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      // Upload the file
      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (error) {
        // SAFEGUARD: Log upload errors
        console.error(`[UPLOAD SAFEGUARD] Upload failed for ${file.name}:`, error);
        throw error;
      }

      // Get the public URL
      const { data: urlData } = supabase.storage
        .from(bucketName)
        .getPublicUrl(filePath);

      // SAFEGUARD: Log successful upload
      console.log(`[UPLOAD SAFEGUARD] Successfully uploaded ${file.type} file: ${file.name}`);

      return {
        url: urlData.publicUrl,
        path: filePath,
        fileName: file.name
      };

    } catch (error: any) {
      console.error('Upload error:', error);
      throw new Error(error.message || 'Upload failed');
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const deleteFile = async (filePath: string, bucketName: string = 'uploads'): Promise<boolean> => {
    try {
      // SAFEGUARD: Log deletion attempt
      console.log(`[DELETE SAFEGUARD] Attempting to delete: ${filePath} from bucket: ${bucketName}`);

      const { error } = await supabase.storage
        .from(bucketName)
        .remove([filePath]);

      if (error) {
        console.error('Delete error:', error);
        return false;
      }

      // SAFEGUARD: Log successful deletion
      console.log(`[DELETE SAFEGUARD] Successfully deleted: ${filePath}`);
      return true;
    } catch (error) {
      console.error('Delete error:', error);
      return false;
    }
  };

  return {
    uploadFile,
    deleteFile,
    uploading,
    uploadProgress
  };
};
